/**
 * QA 2026-10-01 F-28 / F-08: one typed API error for every frontend.
 *
 * Before this module each of about 80 call sites built its own message from a
 * failed response (`Save failed: ${res.status} ${await res.text()}`,
 * `errBody.message ?? ...`, `msg.includes('412')`), so users saw raw JSON
 * envelopes, status numbers and, once, a whole failed SQL statement. Every
 * frontend now turns a failed response into an `ApiError` with
 * `readApiError(res)` and shows `userMessage(err)`: one plain sentence, built
 * from a table keyed by the server's `error` code and the HTTP status, never
 * from the raw body.
 *
 * This file deliberately has no imports so the portal and intake can pull it
 * in (`@rr-crm/shared/api-errors`) without the generated OpenAPI client.
 */
/**
 * A failed API response. `message` is always a plain sentence a person can
 * act on. The parsed body stays on `body` for screens that need route extras
 * (`current` on 412, `existingContactId` on a contact 409, `details`, `issues`);
 * it is never copied into `message`.
 */
export class ApiError extends Error {
    status;
    code;
    fields;
    correlationId;
    retryAfter;
    body;
    /** The server's `message` when it passed the plain-sentence check; otherwise undefined. */
    serverMessage;
    constructor(init) {
        super('');
        this.name = 'ApiError';
        this.status = init.status;
        this.code = init.code ?? `http_${init.status}`;
        this.fields = init.fields ?? [];
        if (init.correlationId !== undefined)
            this.correlationId = init.correlationId;
        if (init.retryAfter !== undefined)
            this.retryAfter = init.retryAfter;
        this.body = init.body;
        const plain = plainSentenceOrUndefined(init.serverMessage);
        if (plain !== undefined)
            this.serverMessage = plain;
        this.message = init.message ?? messageFor(this.status, this.code, this.fields, this.serverMessage);
    }
}
export function isApiError(err) {
    return err instanceof ApiError;
}
// ─── Copy ──────────────────────────────────────────────────────────────────
/** Same text as the CRM's `VIEW_AS_WRITE_BLOCKED_MESSAGE` (apps/crm/src/api/client.ts). */
export const VIEW_AS_READ_ONLY_MESSAGE = 'You are viewing the CRM as a Sales Rep, which is read-only. Exit View As (in the banner at the bottom of the page) to make changes.';
export const ERROR_MESSAGES = {
    viewAsReadOnly: VIEW_AS_READ_ONLY_MESSAGE,
    unauthorized: 'Your sign-in has expired. Sign in again and retry.',
    forbidden: "You don't have permission to do that.",
    notFound: "We couldn't find that. It may have been deleted, or you may not have access.",
    versionConflict: 'Someone else changed this while you were editing. Refresh to see their changes, then try again.',
    conflict: 'That conflicts with an existing record.',
    fileTooLarge: 'That file is too large.',
    invalid: "Some of the information isn't valid.",
    checkFields: 'Check the highlighted fields.',
    rateLimited: 'Too many requests. Wait a moment and try again.',
    server: 'Something went wrong on our side. Please try again.',
    offline: 'You appear to be offline. Check your connection and try again.',
    unknown: 'Something went wrong. Please try again.',
};
// ─── Plain-sentence check ──────────────────────────────────────────────────
/**
 * Text that marks a string as technical output rather than a sentence for a
 * person: JSON, Drizzle's SQL dump, Ajv/Fastify validation phrasing, stack
 * frames, HTML error pages.
 */
const TECHNICAL_MARKERS = [
    /[{}<>]/,
    /Failed query/i,
    /params:/i,
    /\b(body|querystring|params|headers)\//,
    /\bmust be\b/i,
    /must have required property/i,
    /must NOT have/,
    /\n\s*at\s/,
    /\S+\.(?:js|mjs|cjs|ts|tsx):\d+:\d+/,
    /^\s*[A-Za-z]*Error:/,
    // Raw Postgres driver text.
    /\b(?:select|insert\s+into|update|delete\s+from)\s+"/i,
    /out of range for type/i,
    /violates (?:unique|foreign key|check|not-null) constraint/i,
    /duplicate key value/i,
    /\b(?:relation|column) "/i,
    /syntax error at or near/i,
];
const MAX_SENTENCE_LENGTH = 300;
/** True when `value` reads as a plain sentence that is safe to show a person. */
export function isPlainSentence(value) {
    if (typeof value !== 'string')
        return false;
    const s = value.trim();
    if (s.length === 0 || s.length > MAX_SENTENCE_LENGTH)
        return false;
    // A bare code (`not_found`, `version_conflict`, `HTTP_500`) is not a sentence.
    if (!/\s/.test(s))
        return false;
    // Email-template copy names its merge tokens (`{{portalLink}}`); those are
    // words for the admin, not JSON, so they do not trip the brace check.
    const withoutTokens = s.replace(MERGE_TOKEN, 'token');
    return !TECHNICAL_MARKERS.some((re) => re.test(withoutTokens));
}
const MERGE_TOKEN = /\{\{\s*[A-Za-z][A-Za-z0-9_.]*\s*\}\}/g;
function plainSentenceOrUndefined(value) {
    return isPlainSentence(value) ? value.trim() : undefined;
}
/**
 * The server's `message` from a parsed error body when it is a plain
 * sentence, else `fallback`. For screens that keep their own typed errors and
 * only want a safe server sentence.
 */
export function safeServerMessage(body, fallback) {
    const msg = isRecord(body) ? body.message : undefined;
    return plainSentenceOrUndefined(msg) ?? fallback;
}
// ─── Field labels ──────────────────────────────────────────────────────────
/** `employeeCount` -> "Employee count"; `products.0.quantity` -> "Quantity". Mirrors the backend. */
export function fieldLabel(field) {
    const last = field.split('.').filter((p) => !/^\d+$/.test(p)).pop() ?? field;
    const words = last
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}
// ─── The table ─────────────────────────────────────────────────────────────
function messageFor(status, code, fields, serverMessage) {
    // Keyed by code first: these codes mean the same thing whatever the status.
    switch (code) {
        case 'view_as_read_only':
        case 'impersonation_read_only':
            return ERROR_MESSAGES.viewAsReadOnly;
        case 'version_conflict':
            return ERROR_MESSAGES.versionConflict;
        case 'file_too_large':
            return ERROR_MESSAGES.fileTooLarge;
        case 'rate_limited':
            return ERROR_MESSAGES.rateLimited;
        case 'validation_failed':
            return validationMessage(fields, serverMessage);
        default:
            break;
    }
    if (fields.length > 0 && status === 400)
        return validationMessage(fields, serverMessage);
    if (status === 401)
        return ERROR_MESSAGES.unauthorized;
    if (status === 403)
        return ERROR_MESSAGES.forbidden;
    if (status === 404)
        return ERROR_MESSAGES.notFound;
    if (status === 412)
        return ERROR_MESSAGES.versionConflict;
    if (status === 409)
        return serverMessage ?? ERROR_MESSAGES.conflict;
    if (status === 413)
        return ERROR_MESSAGES.fileTooLarge;
    if (status === 422)
        return serverMessage ?? ERROR_MESSAGES.invalid;
    if (status === 429)
        return ERROR_MESSAGES.rateLimited;
    if (status === 400)
        return serverMessage ?? ERROR_MESSAGES.invalid;
    // 5xx: the backend's generic sentence (with its reference id) or one of its
    // known safe sentences; both pass the plain-sentence check.
    if (status >= 500)
        return serverMessage ?? ERROR_MESSAGES.server;
    if (status >= 400)
        return serverMessage ?? ERROR_MESSAGES.unknown;
    return ERROR_MESSAGES.unknown;
}
function validationMessage(fields, serverMessage) {
    if (serverMessage)
        return serverMessage;
    const first = fields[0];
    if (first && isPlainSentence(first.message)) {
        return first.field && first.field !== '(request)'
            ? `${fieldLabel(first.field)}: ${first.message.trim()}`
            : first.message.trim();
    }
    return ERROR_MESSAGES.checkFields;
}
// ─── Reading a failed response ─────────────────────────────────────────────
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function parseFields(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw) {
        if (isRecord(item) && typeof item.field === 'string' && typeof item.message === 'string') {
            out.push({ field: item.field, message: item.message });
        }
    }
    return out;
}
async function readBody(res) {
    // Tolerate empty bodies, non-JSON bodies (an HTML gateway page, plain
    // text) and test doubles that only implement one of text() / json().
    if (typeof res.text === 'function') {
        let text = '';
        try {
            text = await res.text();
        }
        catch {
            return undefined;
        }
        if (!text)
            return undefined;
        try {
            return JSON.parse(text);
        }
        catch {
            return undefined;
        }
    }
    if (typeof res.json === 'function') {
        try {
            return await res.json();
        }
        catch {
            return undefined;
        }
    }
    return undefined;
}
function headerOf(res, name) {
    try {
        return res.headers?.get(name) ?? undefined;
    }
    catch {
        return undefined;
    }
}
function parseRetryAfter(raw) {
    if (!raw)
        return undefined;
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0)
        return n;
    const at = Date.parse(raw);
    if (Number.isNaN(at))
        return undefined;
    return Math.max(0, Math.round((at - Date.now()) / 1000));
}
/**
 * Turn a failed `Response` into an `ApiError`. Never throws; never puts the
 * raw body into `message`.
 */
export async function readApiError(res, opts = {}) {
    const body = await readBody(res);
    const correlationId = headerOf(res, 'x-correlation-id') ?? opts.correlationId;
    const retryAfter = parseRetryAfter(headerOf(res, 'retry-after'));
    return apiErrorFromBody(res.status, body, {
        ...(correlationId !== undefined && { correlationId }),
        ...(retryAfter !== undefined && { retryAfter }),
    });
}
/**
 * The same `ApiError` as `readApiError`, from a body the caller has already
 * parsed (for screens that read the JSON first to look for route extras).
 * The server sentence is `message`, else a string `details` (a few routes
 * put their sentence there); either is used only when it is a plain sentence.
 */
export function apiErrorFromBody(status, body, opts = {}) {
    const rec = isRecord(body) ? body : undefined;
    const code = typeof rec?.error === 'string' && rec.error.trim() ? rec.error.trim() : undefined;
    const correlationId = (typeof rec?.correlationId === 'string' && rec.correlationId) || opts.correlationId;
    const init = {
        status,
        fields: parseFields(rec?.fields),
        body,
    };
    if (code !== undefined)
        init.code = code;
    if (correlationId)
        init.correlationId = correlationId;
    if (opts.retryAfter !== undefined)
        init.retryAfter = opts.retryAfter;
    const serverMessage = isPlainSentence(rec?.message)
        ? rec?.message
        : isPlainSentence(rec?.details)
            ? rec?.details
            : undefined;
    if (typeof serverMessage === 'string')
        init.serverMessage = serverMessage;
    return new ApiError(init);
}
// ─── Any error -> one sentence ─────────────────────────────────────────────
/** `OfflineError` from each app's `api/offline.ts`, or the browser's fetch network failure. */
export function isOfflineError(err) {
    if (!(err instanceof Error))
        return false;
    if (err.name === 'OfflineError' || err.kind === 'OfflineError')
        return true;
    return (err.name === 'TypeError' &&
        /failed to fetch|networkerror|network request failed|load failed/i.test(err.message));
}
/**
 * One plain sentence for any thrown value: no JSON, no status numbers, no
 * stack. An `ApiError` already carries its sentence; offline and network
 * failures get the offline sentence; anything else gets the generic sentence
 * (an unknown error's own `message` is never shown).
 */
export function userMessage(err, opts = {}) {
    if (err instanceof ApiError)
        return err.message;
    if (isOfflineError(err))
        return ERROR_MESSAGES.offline;
    if (opts.action && opts.action.trim()) {
        return `We couldn't ${opts.action.trim()}. Please try again.`;
    }
    return ERROR_MESSAGES.unknown;
}
/** `fields` as a field -> message record (first message per field). Empty for anything but an `ApiError`. */
export function fieldErrorsFrom(err) {
    const out = {};
    if (!(err instanceof ApiError))
        return out;
    for (const f of err.fields) {
        if (!(f.field in out) && isPlainSentence(f.message))
            out[f.field] = f.message.trim();
    }
    return out;
}
/** True for a 4xx: a retry would get the same answer (F-33 uses this to skip retries). */
export function isClientError(err) {
    return err instanceof ApiError && err.status >= 400 && err.status < 500;
}
//# sourceMappingURL=errors.js.map